import { z } from "zod";
//#region src/org-members.ts
/**
* core-api route for {@link OrgMembersClient.list}. Service-bearer callers only: the route sits behind
* the global `DpJwtAuthGuard` with no class-level permission decorator (ruling R-024), and the handler
* resolves the calling app from the `machine_principals` row the bearer's `sub` names (R-010). It takes
* the organization from the end-user token the app forwards under `WORK_END_USER_TOKEN_HEADER`
* (`x-zion-end-user-token`), verified by core-api, and from the bearer's `orgId` claim when no token
* is forwarded (R-023). It then requires that organization's consent to the app holding
* {@link ORG_MEMBERS_READ_PERMISSION} (R-022). It accepts no organization or app identifier
* (overview D3, D4, D5).
*/
const ORG_MEMBERS_PATH = "/v1/org-members";
/** The route parameter of the per-member read; the controller binds `@Get(':memberId')` with it (R-006). */
const ORG_MEMBER_ID_PARAM = "memberId";
/**
* The path of {@link OrgMembersClient.get} for one member: {@link ORG_MEMBERS_PATH} plus the encoded id.
* Same guard chain as the list. An id the organization does not hold answers 200 with `member: null`,
* so a 404 from this path keeps meaning `route-unavailable` (ruling R-006).
*/
function orgMemberPath(memberId) {
	return `${ORG_MEMBERS_PATH}/${encodeURIComponent(memberId)}`;
}
/**
* The permission that gates {@link ORG_MEMBERS_PATH}. An app requests it in `app.json`
* `platformPermissions[]`, and an organization consents when it onboards the app (rulings R-018,
* R-020, R-022); it is the first member of `REQUESTABLE_PLATFORM_PERMISSION_KEYS`. A platform key, so
* `isReservedOrgPermissionKey` refuses it as an app-defined key by prefix, and absent from
* `ORG_PERMISSION_CATALOG`, so the role editor never lists it and no role carries it. No bearer claim
* carries it either: core-api's handler reads the organization's consent row at request time
* (overview D2, D5).
*/
const ORG_MEMBERS_READ_PERMISSION = "platform:org-members:read";
/**
* Largest `pageSize`, and the fixed cap on `pendingInvitations`. Derivation: `AUDIT_LOG_MAX_PAGE_SIZE`
* in core-api's audit-log query schema is 200, the largest page the repo serves today (overview D10).
*/
const ORG_MEMBERS_MAX_PAGE_SIZE = 200;
/**
* Largest `page`. Derivation: the same ceiling the audit-log query schema puts on `page`, so
* `offset = (page - 1) * pageSize` stays under 2^31 (overview D10).
*/
const ORG_MEMBERS_MAX_PAGE = 1e5;
/**
* Longest `search`. Derivation: the bound `createOrgRoleSchema` uses for a role name, 256, which also
* covers the 254-character maximum of an email address (overview D10).
*/
const ORG_MEMBERS_MAX_SEARCH_LENGTH = 256;
/**
* The characters a `search` may carry: anything outside the Unicode control category `Cc`, which
* holds C0, DEL and the C1 controls. A NUL bound into an `ILIKE` pattern makes Postgres raise
* `22021` and the route answer 500, so the bound is on the wire and the query never sees one
* (security-code-stage-1 S5-02). The property escape names the class without a control-character
* escape, so `no-control-regex` has nothing to flag (S5R3-01). Linear-time: one class, no
* quantifier nesting.
*/
const ORG_MEMBERS_SEARCH_PATTERN = /^\P{Cc}+$/u;
/**
* `pageSize` when the caller sends none. lead: guess (overview §6): 50 is the page the requesting
* console asks for in its first call, and a quarter of {@link ORG_MEMBERS_MAX_PAGE_SIZE}.
*/
const ORG_MEMBERS_DEFAULT_PAGE_SIZE = 50;
/**
* The query string of {@link ORG_MEMBERS_PATH}. `.strict()`: an unknown key (an `appId`, an `orgId`)
* is refused with 400 before any read (security-audit chain C hop 1, chain E hop 1). `page` and
* `pageSize` are coerced from their string form. `search` is a case-insensitive literal substring over
* email and full name: `%` and `_` match themselves, because the server escapes them (ruling R-004).
* An empty string is refused, so a caller omits the key instead. A control character is refused
* ({@link ORG_MEMBERS_SEARCH_PATTERN}), so a NUL never reaches the query (S5-02).
*/
const organizationMembersQuerySchema = z.object({
	page: z.coerce.number().int().min(1).max(ORG_MEMBERS_MAX_PAGE).default(1),
	pageSize: z.coerce.number().int().min(1).max(200).default(50),
	search: z.string().min(1).max(256).regex(ORG_MEMBERS_SEARCH_PATTERN).optional()
}).strict();
/**
* The calling app's view of one member. Both fields describe the calling app and no other
* (security-audit chain C).
*
* `access` is true when two conditions hold:
* - the organization holds a live entitlement for the calling app;
* - core-api's visibility rule keeps the app for the member's own first-party login.
*
* The rule is the one that `GET /v1/apps` runs for the shell list (#4491). Ruling R-055 of
* `2026-09-07-org-members-read-for-apps` states it, and R-055 amends R-008 and R-014. The rule has
* five arms:
* 1. the member's built-in role is `owner`;
* 2. the member holds the app-access policy `all`. A role the member holds carries it, or the
*    built-in slug names no seeded definition whose `isBuiltin` is true;
* 3. the app is a first-party app (`FIRST_PARTY_APPS`), and its `visibleToOrgRoles` names one of
*    the member's roles. Those roles are the built-in role and every direct and group-reached role;
* 4. a role the member holds has an active `<slug>:read` grant on the app, with both halves, and
*    the app declares `<slug>:read`. `<slug>` is the app's slug;
* 5. a role the member holds lists a flat permission that admits `<slug>:read`, while
*    `ZION_DP_APP_PERMS_TYPED_ONLY` is off.
*
* core-api stores a role's app list (`appAccessIds`), and no arm of the visibility rule reads it.
* One check outside the rule reads it: core-api refuses a machine caller that confers a role whose
* list is not empty. The shell list also needs a channel that serves a release of the app to the
* member, and `access` covers no channel. The admin app says the same: "Each holder also needs a
* channel that serves the app."
*
* `permissions` holds each permission key of the calling app that a grant gives to one of the
* member's roles, deduplicated and sorted. A grant counts only when both of its halves exist. The calling app
* must declare the key, the filter that `PrincipalPermissionResolver.buildGrantClaims` applies. So
* the member's app token for the calling app carries every key in the list.
*/
const organizationMemberAppSchema = z.object({
	access: z.boolean(),
	permissions: z.array(z.string().min(1))
}).strict();
/**
* One member of the calling app's organization. Built from `userPublicColumns` and mapped by name:
* `fullName` is the row's display name, `isActive` is `status === 'active'`. `roles` is the member's
* effective role set: the built-in role on the user row, direct `principal_roles` rows and roles reached
* through group membership, deduplicated by slug and sorted. `groupIds` is direct membership only.
* `.strict()` at every level: `googleSub`, `forcePasswordChange`, avatar, sessions and passkeys can
* never appear, because an added key fails the producer's own `parse()` (security-audit chain D).
*/
const organizationMemberSchema = z.object({
	id: z.string().uuid(),
	email: z.string().min(1),
	fullName: z.string(),
	isActive: z.boolean(),
	lastLoginAt: z.string().datetime().nullable(),
	createdAt: z.string().datetime(),
	updatedAt: z.string().datetime(),
	roles: z.array(z.string().min(1)),
	groupIds: z.array(z.string().uuid()),
	app: organizationMemberAppSchema
}).strict();
/** The route parameter of the per-member read. `.strict()`; a non-uuid is 400 (chain E hop 5, R-006). */
const orgMemberIdParamSchema = z.object({ [ORG_MEMBER_ID_PARAM]: z.string().uuid() }).strict();
/**
* The response of the per-member read. `organizationId` is the target organization (R-023), as on the page
* (R-002). `member` is the one member, or `null` when the organization holds no member with that id,
* a well-formed id from another organization included; the status is 200 either way (R-006).
*/
const organizationMemberReadSchema = z.object({
	organizationId: z.string().uuid(),
	member: organizationMemberSchema.nullable()
}).strict();
/**
* One pending invitation of the organization. `invitedAt` is the row's `createdAt`; `expired` is
* computed as `expiresAt <= now` because the table stores only pending, accepted and revoked
* (overview D7). Built from `invitationPublicColumns`, so the invitation `token` never appears.
*/
const pendingInvitationSchema = z.object({
	id: z.string().uuid(),
	email: z.string().min(1),
	invitedAt: z.string().datetime(),
	expiresAt: z.string().datetime(),
	expired: z.boolean()
}).strict();
/**
* The response of {@link ORG_MEMBERS_PATH}. `organizationId` is the organization the page lists, the
* verified `orgId` of the forwarded end-user token, else the bearer's `orgId` claim (R-023), and never a
* request value, so a multi-tenant caller compares it with its
* request's organization without decoding the token (ruling R-002). `items` is one page of members;
* `total` counts every member the `search` matches, so `items.length <= pageSize` and the last page
* is `ceil(total / pageSize)`. `pendingInvitations` is every pending invitation the same `search`
* matches, up to {@link ORG_MEMBERS_MAX_PAGE_SIZE}, whatever `pageSize` the caller sent;
* `pendingInvitationsTruncated` is true when more exist. `items` are ordered by `createdAt` descending
* then `id` ascending, and `pendingInvitations` by `invitedAt` descending then `id` ascending, on the
* server and in the Fake alike (ruling R-005). The same organization on every page
* (chain A).
*/
const organizationMembersPageSchema = z.object({
	organizationId: z.string().uuid(),
	items: z.array(organizationMemberSchema),
	pendingInvitations: z.array(pendingInvitationSchema).max(200),
	pendingInvitationsTruncated: z.boolean(),
	page: z.number().int().min(1).max(ORG_MEMBERS_MAX_PAGE),
	pageSize: z.number().int().min(1).max(200),
	total: z.number().int().min(0)
}).strict();
/**
* Why a call failed, from the client's point of view. `route-unavailable`: the data plane answered
* 404, so it runs a core-api older than this contract (overview D9, D11). `unauthorized`: 401, the
* bearer was missing or refused. `forbidden`: 403, one of the handler's refusals (rulings R-010, R-023,
* R-027): the bearer is not a service principal of a catalogued app; the forwarded end-user token failed
* its bound or its verification; the target organization has no live entitlement for the app; or it has
* not consented to the app holding {@link ORG_MEMBERS_READ_PERMISSION}, or no kept version requests it.
* The body names no condition. `invalid-args`: the client refused the query against the bounds
* before sending (`status` undefined), or core-api answered 400 (`status: 400`); ruling R-001.
* `malformed-response`: a 2xx body failed {@link organizationMembersPageSchema}. `transport`: the
* request got no HTTP response, or a status this list does not name, 5xx included.
*/
const ORG_MEMBERS_CLIENT_ERROR_REASONS = [
	"route-unavailable",
	"unauthorized",
	"forbidden",
	"invalid-args",
	"malformed-response",
	"transport"
];
/**
* The one error {@link OrgMembersClient.list} rejects with. `reason` is the branch a caller switches
* on; `status` is the HTTP status when there was one; `cause` is the underlying error when there was
* one (a `ZodError` for `malformed-response`, the `fetch` rejection for `transport`). Shaped like
* `InvalidPageCursorError`: a class, so `instanceof` works across the SDK packages.
*/
var OrgMembersClientError = class extends Error {
	name = "OrgMembersClientError";
	reason;
	status;
	constructor(reason, message, options) {
		super(message, { cause: options?.cause });
		this.reason = reason;
		this.status = options?.status;
	}
};
//#endregion
export { ORG_MEMBERS_CLIENT_ERROR_REASONS, ORG_MEMBERS_DEFAULT_PAGE_SIZE, ORG_MEMBERS_MAX_PAGE, ORG_MEMBERS_MAX_PAGE_SIZE, ORG_MEMBERS_MAX_SEARCH_LENGTH, ORG_MEMBERS_PATH, ORG_MEMBERS_READ_PERMISSION, ORG_MEMBERS_SEARCH_PATTERN, ORG_MEMBER_ID_PARAM, OrgMembersClientError, orgMemberIdParamSchema, orgMemberPath, organizationMemberAppSchema, organizationMemberReadSchema, organizationMemberSchema, organizationMembersPageSchema, organizationMembersQuerySchema, pendingInvitationSchema };

//# sourceMappingURL=org-members.js.map